import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';

import { makeGridPopupContainer } from '../../containers/GridPopup/GridPopup';
import { Bem } from '../../service/Bem/Bem';
import { GroupsHelpers } from '../../service/GroupsHelpers/GroupsHelpers';
import { Lang, Tld } from '../../typings/common';
import {
    CampaignType,
    GdGroupStatus,
    RejectReasonsData
} from '../../typings/domain';
import { GdAdGroupPrimaryStatus } from '../../typings/graphql-grid';
import {
    ModerationRejectReasons,
    ModerationRejectReasonsProps
} from '../ModerationRejectReasons/ModerationRejectReasons';
import { Shape2, Shape2Icon, Shape2Theme } from '../Shape2/Shape2';

import './GridGroupStatusCell.css';
import * as keyset from './GridGroupStatusCell.i18n';

const i18n = i18nFactory(keyset);

type GridGroupStatusCellProps = {
    groupId: string;
    campaignType: CampaignType;
    status: GdGroupStatus;
    lang: Lang;
    tld: Tld;
    clientRegion: string;
    isOperatorAgency: boolean;
    isOperatorSelfAgency: boolean;
    rejectReasons?: RejectReasonsData;
    getModerationRejectReasons: (groupId: string) => void;
    hasChangeAdGroupForModerationRecommendation?: boolean;
    isTextVisible: boolean;
};

const ModerationRejectReasonsPopupContainer = makeGridPopupContainer<
    ModerationRejectReasonsProps
>(ModerationRejectReasons);

const BLOCK = 'grid-group-status-cell';

export class GridGroupStatusCell extends React.PureComponent<
    GridGroupStatusCellProps
> {
    render() {
        const { status, isTextVisible } = this.props;
        const title = GroupsHelpers.getTitleForGroupStatus(
            status.primaryStatus
        );

        return (
            <div className={Bem.cls(BLOCK)}>
                <div
                    className={Bem.cls({
                        block: BLOCK,
                        elem: 'status',
                        mods: {
                            type: Bem.beminize(status.primaryStatus)
                        }
                    })}
                >
                    {this.getStatusIcon(title)}
                    {isTextVisible && title}
                </div>
                {isTextVisible && this.getHint()}
            </div>
        );
    }

    private getStatusIcon(title: string) {
        const { status } = this.props;

        switch (status.primaryStatus) {
            case GdAdGroupPrimaryStatus.ACTIVE:
                return (
                    <Shape2
                        icon={Shape2Icon.statusRunning}
                        theme={Shape2Theme.light}
                        width={12}
                        height={12}
                        cls={Bem.cls(BLOCK, 'icon')}
                        title={title}
                    />
                );
            case GdAdGroupPrimaryStatus.REJECTED:
                return (
                    <Shape2
                        icon={Shape2Icon.statusStopped}
                        theme={Shape2Theme.light}
                        width={12}
                        height={12}
                        cls={Bem.cls(BLOCK, 'icon')}
                        title={title}
                    />
                );
            case GdAdGroupPrimaryStatus.MODERATION:
                return (
                    <Shape2
                        icon={Shape2Icon.statusPending}
                        theme={Shape2Theme.light}
                        width={12}
                        height={12}
                        cls={Bem.cls(BLOCK, 'icon')}
                        title={title}
                    />
                );
            case GdAdGroupPrimaryStatus.STOPPED:
            case GdAdGroupPrimaryStatus.ARCHIVED:
            case GdAdGroupPrimaryStatus.DRAFT:
            default:
                return (
                    <Shape2
                        icon={Shape2Icon.statusEnded}
                        theme={Shape2Theme.light}
                        width={12}
                        height={12}
                        cls={Bem.cls(BLOCK, 'icon')}
                        title={title}
                    />
                );
        }
    }

    private getHint() {
        const {
            status,
            campaignType,
            rejectReasons,
            lang,
            tld,
            clientRegion,
            isOperatorSelfAgency,
            isOperatorAgency,
            hasChangeAdGroupForModerationRecommendation
        } = this.props;

        let hint = GroupsHelpers.getHintForGroupStatus(campaignType, status);

        const isRejectedOnModeration =
            status.primaryStatus === GdAdGroupPrimaryStatus.REJECTED ||
            (status.primaryStatus === GdAdGroupPrimaryStatus.STOPPED &&
                hasChangeAdGroupForModerationRecommendation);

        if (isRejectedOnModeration) {
            hint = i18n('rejected-on-moderation');
        }

        if (!hint) {
            return null;
        }

        return (
            <div className={Bem.cls(BLOCK, 'hint')} title={hint}>
                {hint}

                {isRejectedOnModeration ? (
                    <ModerationRejectReasonsPopupContainer
                        cls={Bem.cls(BLOCK, 'hint-info')}
                        reasons={rejectReasons && rejectReasons.items}
                        contactEmail={
                            rejectReasons && rejectReasons.contactEmail
                        }
                        clientRegion={clientRegion}
                        isOperatorAgency={isOperatorAgency}
                        isOperatorSelfAgency={isOperatorSelfAgency}
                        lang={lang}
                        tld={tld}
                        error={rejectReasons && rejectReasons.error}
                        onPopupOpen={this.onModerationRejectReasonsPopupOpen}
                    />
                ) : null}
            </div>
        );
    }

    private onModerationRejectReasonsPopupOpen = () => {
        const {
            rejectReasons,
            getModerationRejectReasons,
            groupId
        } = this.props;

        if (rejectReasons === undefined || rejectReasons.error) {
            getModerationRejectReasons(groupId);
        }
    };
}
